import { useEffect, useRef, useState } from 'react'
import { useStateValue } from 'providers'
import { refreshAccessToken } from 'actions/api'
import { queryCache, DEFAULT_STALE_TIME } from './queryCache'
const API_URL = process.env.API_URL

export const useGetQuery = ({
  enabled = true,
  files,
  method = 'GET',
  payload,
  staleTime = DEFAULT_STALE_TIME,
  url
}) => {
  const [{ session }, dispatch] = useStateValue()
  const { token } = session

  const [isEnabled, setEnabled] = useState(enabled)

  const [path, setPath] = useState(url)

  const [shouldFetch, triggerFetch] = useState()
  const forceRef = useRef(false)

  // External refetch()/reFetch() always bypasses the cache's freshness check —
  // matches the pre-cache behavior where every external trigger caused a real fetch.
  const publicRefetch = () => {
    forceRef.current = true
    triggerFetch(true)
  }

  const [state, setState] = useState({
    error: false,
    isFetching: true,
    reFetch: publicRefetch,
    refetch: publicRefetch,
    setEnabled,
    setPath
  })

  const didMountPath = useRef(false)
  useEffect(() => {
    // Skip the mount-time run only — `path` is already seeded from `url` via
    // useState, and the `enabled` effect below handles the initial fetch.
    // Gating this on `state.isFetching` instead (as before) dropped the
    // trigger whenever a path changed while the previous path's fetch was
    // still in flight, with nothing left to re-fire it once that settled.
    if (!didMountPath.current) {
      didMountPath.current = true
      return
    }
    triggerFetch(true)
  }, [path])

  useEffect(() => {
    setEnabled(enabled)
  }, [enabled])

  useEffect(() => {
    const rf = async () => {
      await triggerFetch(!shouldFetch)
    }
    rf().then()
  }, [isEnabled])

  const options = {
    method,
    body: payload
      ? JSON.stringify({
        ...payload
      })
      : files || null,
    credentials: 'include',
    headers: {
      'Content-Type': files ? 'multipart/form-data;' : 'application/json'
    }
  }

  if (token) options.headers.Authorization = `JWT ${token}`

  // Resolves this render's fetch, returning { data } or { error } rather than
  // writing state directly — results flow through queryCache's subscription so
  // every mounted instance of this key (not just whichever one kicked off the
  // request) gets updated consistently.
  const doNetworkFetch = async () => {
    try {
      const res = await fetch(`${API_URL}${path}`, options)
      if (res.status === 401) {
        const refreshed = await refreshAccessToken()
        if (refreshed) {
          const retryRes = await fetch(`${API_URL}${path}`, options)
          const json = await retryRes.json()
          return { data: json }
        }
        dispatch({ type: 'LOGOUT' })
        return { error: 'Session expired' }
      }
      const json = await res.json()
      return { data: json }
    } catch (e) {
      return { error: e }
    }
  }

  const cacheKey = queryCache.keyFor(path, payload)

  useEffect(() => {
    if (!url) return
    const unsubscribe = queryCache.subscribe(cacheKey, (reason) => {
      const entry = queryCache.getEntry(cacheKey)
      if (reason === 'settled') {
        setState((s) => ({ ...s, isFetching: false, data: entry.data, error: entry.error }))
      } else if (reason === 'invalidated' && isEnabled) {
        queryCache.fetchAndStore(cacheKey, doNetworkFetch)
      }
    })
    return unsubscribe
  }, [cacheKey, isEnabled])

  if (!url) return

  useEffect(() => {
    if (shouldFetch == null) return
    if (!isEnabled) return

    const force = forceRef.current
    forceRef.current = false

    const entry = queryCache.getEntry(cacheKey)
    if (!force && entry.updatedAt) {
      // Cache hit — serve instantly, no spinner. Stale entries also kick a
      // silent background revalidation (deduped against any other subscriber
      // already revalidating the same key).
      setState((s) => ({ ...s, isFetching: false, data: entry.data, error: entry.error }))
      if (queryCache.isStale(cacheKey, staleTime)) {
        queryCache.fetchAndStore(cacheKey, doNetworkFetch)
      }
    } else {
      setState((s) => ({ ...s, isFetching: true }))
      queryCache.fetchAndStore(cacheKey, doNetworkFetch)
    }

    triggerFetch(null)
  }, [shouldFetch])

  return state
}
